import {
  ChangeEvent,
  memo,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAtom } from 'jotai';
import { keyBy } from 'lodash-es';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { AiOutlineQuestionCircle } from 'react-icons/ai';
import { CgFileDocument } from 'react-icons/cg';
import { useBlocker } from 'react-router';
import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ColumnDef } from '@tanstack/react-table';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import { useUpdateDocumentForm } from '~/core/shared/api/documents';
import { DateTimeDisplayMode } from '~/core/shared/components/DateTimeDisplay/constants/DateTimeDisplayMode';
import { formatDateTime } from '~/core/shared/components/DateTimeDisplay/logic/formatDateTime';
import {
  transformBlockValuesIn,
  transformBlockValuesOut,
  transformFieldsIntoBlocks,
} from '~/core/shared/fields/FieldUtils';
import { useFormResolver, useRequiredParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Tooltip } from '~/core/shared/lib/chakraUi/components';
import { DocumentFormBlock, DocumentFormField } from '~/core/shared/types';
import { handleSuccess } from '~/core/shared/utils/admin';
import { dirtyValues } from '~/core/shared/utils/general';
import { isDirtyAtom } from '../../contexts/dirty.context';
import { DocumentFormContext } from '../application';
import { fieldMatchesSearch } from './logic/fieldMatchesSearch';
import { SearchInput } from './CrudSearch.component';
import { FieldTable } from './FieldTable';
import {
  FormFieldRenderer,
  supportedBlockRenderTypes,
} from './FormFieldRenderer.component';
import { generateSchema } from './GenerateSchema.component';
import { DisplayActions } from './SidebarPanel.component';
import { UnsavedModal } from './UnsavedModal.component';
import { useBackendErrors } from './useBackendErrors.hook';

type DefaultType = {
  [x: string]: string;
};

type Props = {
  block: DocumentFormBlock;
};

const ErrorDisplayComponent = ({ error }: { error: string | undefined }) => {
  if (!error) {
    return null;
  }
  return <Text color="red.500">{error}</Text>;
};

export const ErrorDisplay = memo(ErrorDisplayComponent);

const getColumns = (
  getError: (key: string) => string | undefined,
): ColumnDef<DocumentFormField>[] => [
  {
    accessorKey: 'name',
    cell({ row }) {
      return row.original.label ? (
        <Stack overflow="hidden" textOverflow="ellipsis">
          <Text>
            {row.original.label}{' '}
            <Text as="span" color="red.500">
              {row.original.required ? '*' : null}
              {row.original.readOnly ? '(Read only)' : null}
              {row.original.tooltip && (
                <Tooltip
                  content={row.original.tooltip}
                  positioning={{ placement: 'right' }}
                  showArrow
                >
                  <Icon
                    as={AiOutlineQuestionCircle}
                    color="red.500"
                    cursor="pointer"
                  />
                </Tooltip>
              )}
            </Text>
          </Text>

          <ErrorDisplay error={getError(row.original.name)} />
        </Stack>
      ) : null;
    },
    header: 'Attribute',
  },
  {
    accessorKey: 'value',
    cell: ({ row }) => <FormFieldRenderer field={row.original} />,
    header: 'Value',
  },
  {
    accessorKey: 'updated',
    cell({ row }) {
      return row.original.updated ? (
        <Text>
          {formatDateTime(
            row.original.updated,
            DateTimeDisplayMode.DateTimeAmPm,
          )}
        </Text>
      ) : (
        <Text>-</Text>
      );
    },
    header: 'Updated',
  },
  // @TODO: this still needs to be implemented
  // {
  //   accessorKey: 'author',
  //   cell({ row }) {
  //     return (
  //       <Flex alignItems="center">
  //         <Avatar.Root color="white" p={4} size="sm">
  //           <Avatar.Fallback
  //             name="Cameron Williamson"
  //           />
  //         </Avatar.Root>
  //          Cameron Williamson
  //        </Flex>
  //      );
  //    },
  //    header: 'Author',
  //  },
];

export const MainPanel = ({ block }: Props): ReactElement => {
  // TODO: 'doc' is incorrect here
  const { updateDocumentForm, isPending } = useUpdateDocumentForm({
    definition: 'doc',
  });
  const { formIdentifier, updateFormBlocksById, refetchForm, refreshOnSave } =
    useContext(DocumentFormContext);
  const { uuid } = useRequiredParams(['uuid']);
  const blockFields = block.fields;

  const [searchValue, setSearchValue] = useState<string>('');
  const handleInputChange = useEventHandler(
    (event: ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setSearchValue(inputValue);
    },
  );

  const currentSchema = useMemo(
    () => generateSchema(blockFields),
    [blockFields],
  );

  const blocks = useMemo(
    () =>
      transformFieldsIntoBlocks(
        blockFields.filter((field) =>
          supportedBlockRenderTypes.includes(field.type),
        ),
      ),
    [blockFields],
  );

  // TODO: @haxxxton refactor to use form blocks
  const resolver = useFormResolver(currentSchema);
  const form = useForm<DefaultType>({
    defaultValues: transformBlockValuesIn(blocks),
    resolver,
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, dirtyFields, isDirty },
  } = form;

  const filteredBlockFields = useMemo(() => {
    if (searchValue) {
      return blockFields.filter((field: DocumentFormField) =>
        fieldMatchesSearch(field, searchValue.toLowerCase()),
      );
    }
    return blockFields;
  }, [searchValue, blockFields]);

  const unsavedChangesModalApi = useOpenClose();

  const {
    getError,
    clearBackendErrors,
    backendErrors,
    setBackendErrorsFromResponse,
  } = useBackendErrors(errors);

  useEffect(() => {
    // reset form when block and fields change
    clearBackendErrors();
    reset(transformBlockValuesIn(blocks));
  }, [blocks]);

  // Table column definition
  const columns = useMemo<ColumnDef<DocumentFormField>[]>(
    () => getColumns(getError),
    [errors, backendErrors, control, blockFields],
  );

  // Error formatting
  const colorRows = useEventHandler((row: any) => {
    const blueBackground = '#f1f8fc';
    const errorBackground = '#FED7D7';

    if (Object.keys(errors).some((key) => row.original.name.includes(key))) {
      return errorBackground;
    }

    if (
      Object.keys(dirtyFields).some((dirtyKey) =>
        row.original.name.includes(dirtyKey),
      )
    ) {
      return blueBackground;
    }
    return 'white';
  });

  const [, setGlobalDirty] = useAtom(isDirtyAtom);

  const blocker = useBlocker(isDirty);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      unsavedChangesModalApi.onOpen();
    }
  }, [blocker.state]);

  const onSubmit = useEventHandler(
    async (formValues: FieldValues): Promise<void> => {
      clearBackendErrors();
      const dirtyFiltered = transformBlockValuesOut(
        dirtyValues(dirtyFields, formValues),
        blocks,
      );

      try {
        const response = await updateDocumentForm({
          documentId: uuid,
          data: dirtyFiltered,
          formIdentifier,
          path: block.meta.path,
        });

        if (refreshOnSave || response?.data.meta.scoped) {
          await refetchForm();
        } else if (block.meta.id && response?.data?.content) {
          updateFormBlocksById(
            keyBy(response.data.content, (formBlock) => formBlock.meta.id),
          );
          reset(form.getValues());
        }
        handleSuccess('Update successful');
      } catch (error: any) {
        if (error?.response?.status === 422) {
          setBackendErrorsFromResponse(error?.response?.data);
        }
      }
    },
  );

  const handleConfirmLeave = useEventHandler((): void => {
    blocker.proceed?.();
    unsavedChangesModalApi.onClose();
  });

  useEffect(() => {
    setGlobalDirty(isDirty);
  }, [isDirty, setGlobalDirty]);

  return block.meta.title !== '' ? (
    <>
      <FormProvider {...form}>
        <Flex as="form" direction="column" onSubmit={handleSubmit(onSubmit)}>
          <HStack height="80px" p={4}>
            <Heading
              lineClamp={1}
              minWidth="200px"
              size="md"
              whiteSpace="nowrap"
              wordBreak="break-all"
            >
              {block.meta?.title || 'Block'}
            </Heading>
            <SearchInput
              handleInputChange={handleInputChange}
              placeholder="Search attribute or value"
            />
            <HStack>
              {block.meta.actions.length > 0 &&
                DisplayActions(block?.meta?.actions, block, true, 'md')}
              <Button
                colorPalette="primary"
                disabled={!isDirty}
                fontSize="md"
                loading={isPending}
                onClick={handleSubmit(onSubmit)}
                size="md"
                type="submit"
                variant="solid"
              >
                Save changes
              </Button>
            </HStack>
          </HStack>

          <FieldTable
            columns={columns}
            data={filteredBlockFields}
            getRowProps={(row) => ({
              style: {
                backgroundColor: colorRows(row),
                px: 4,
              },
            })}
            rowKey={(row) => `${block.meta.blockId}-${row.id}`}
            variant="crudBrand"
          />
        </Flex>
      </FormProvider>
      <UnsavedModal
        close={handleConfirmLeave}
        modalApi={unsavedChangesModalApi}
      />
    </>
  ) : (
    <Box background="gray.100" height="100%">
      <Center height="100%">
        <VStack>
          <Icon as={CgFileDocument} boxSize={12} color="gray.500" />
          <Text color="gray.500">Select a document to view and edit</Text>
        </VStack>
      </Center>
    </Box>
  );
};
